import { type ShortIdProviderProps } from '@air/provider-short-id';

/** One entry of a collection share link's flat sub-page list — a nested collection or a bare link. */
export type ShowcaseSubPage = NonNullable<ShortIdProviderProps['presentationPages']>['subPages'][number];

/**
 * The routable sub-page variant. Link sub-pages navigate away to an external URL, so only
 * collection sub-pages get a slug and a route on the public share link.
 */
export type ShowcaseSubPageCollection = Extract<ShowcaseSubPage, { type: 'collection' }>;

export type ShowcaseSubPageWithSlug = {
  slug: string;
  subPage: ShowcaseSubPageCollection;
};

/**
 * Keeps a derived slug from swallowing the whole URL when an author names a page with a sentence.
 * Truncation can create collisions, which the dedupe pass below resolves the same way it resolves
 * genuinely duplicate names.
 */
export const SHOWCASE_SUB_PAGE_SLUG_MAX_LENGTH = 60;

/** Used when a name slugifies to nothing at all (emoji-only, punctuation-only, CJK-only names). */
export const SHOWCASE_SUB_PAGE_FALLBACK_SLUG = 'page';

// Matches the `/page/{slug}` segment on BOTH share-link viewer bases — the live `/a/{shortId}`
// pages and the editor's owner-only `/showcases/preview/{showcaseId}` sibling routes — and
// nothing else: a `/page/` segment on any other route (however a board or asset happens to be
// named) must not resolve as a sub-page.
const SHOWCASE_SUB_PAGE_PATH_REG_EXP = /^\/(?:a\/[^/?#]+|showcases\/preview\/[^/?#]+)\/page\/([^/?#]+)/;

/**
 * Derives a URL-safe slug from a sub-page's name. Not unique on its own — two pages can share a
 * name, and truncation can collapse two long names onto the same stem — so callers that need a
 * routable slug must go through `resolveShowcaseSubPageSlugs`.
 */
export const slugifyShowcaseSubPageName = (name: string) => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SHOWCASE_SUB_PAGE_SLUG_MAX_LENGTH)
    // The length cap can land mid-separator, leaving a trailing dash the first trim couldn't see.
    .replace(/-+$/g, '');

  return slug || SHOWCASE_SUB_PAGE_FALLBACK_SLUG;
};

/**
 * Assigns every collection sub-page a slug that is unique across the link, walking `subPages` in
 * list order so the same authored data always produces the same URLs (a share link's URLs must not
 * change just because the page was re-rendered). Repeats of an already-taken slug get `-2`, `-3`,
 * … — including the case where a later page's own name slugifies onto a suffix already handed out.
 *
 * Link sub-pages are skipped rather than counted: they have no route, and letting an external
 * link's label consume a suffix would make a collection page's URL depend on unrelated data.
 */
export const resolveShowcaseSubPageSlugs = (subPages: readonly ShowcaseSubPage[]): ShowcaseSubPageWithSlug[] => {
  const usedSlugs = new Set<string>();

  return subPages.reduce<ShowcaseSubPageWithSlug[]>((resolved, subPage) => {
    if (subPage.type !== 'collection') {
      return resolved;
    }

    const baseSlug = slugifyShowcaseSubPageName(subPage.name);
    let slug = baseSlug;
    let suffix = 1;

    while (usedSlugs.has(slug)) {
      suffix += 1;
      slug = `${baseSlug}-${suffix}`;
    }

    usedSlugs.add(slug);
    resolved.push({ slug, subPage });

    return resolved;
  }, []);
};

export type FindShowcaseSubPageBySlugParams = {
  /** The decoded slug segment from the URL, or null when the viewer is on the link's root page. */
  slug: string | null;
  subPages: readonly ShowcaseSubPage[];
};

/**
 * Resolves the slug segment of a share-link URL to the sub-page it addresses.
 *
 * A raw `collectionId` is accepted in the slug position as a fallback so a link pasted before a
 * page was renamed — or minted by a surface that doesn't derive slugs — still resolves. An
 * unmatched slug returns null, which callers render as the root page: a share link should never
 * 404 on a stale sub-page URL.
 */
export const findShowcaseSubPageBySlug = ({
  slug,
  subPages,
}: FindShowcaseSubPageBySlugParams): ShowcaseSubPageCollection | null => {
  if (!slug) {
    return null;
  }

  const resolved = resolveShowcaseSubPageSlugs(subPages);
  const matchedBySlug = resolved.find((entry) => entry.slug === slug);

  if (matchedBySlug) {
    return matchedBySlug.subPage;
  }

  return resolved.find((entry) => entry.subPage.collectionId === slug)?.subPage ?? null;
};

/**
 * Reads the sub-page slug out of a public share-link path, mirroring
 * `createShortUrlShowcaseSubPageRoute`. Returns null on every other path — including the share
 * link's own root, board, and clip routes — so non-collection surfaces are unaffected.
 */
export const getShowcaseSubPageSlugFromPath = (pathname: string): string | null => {
  const match = pathname?.match(SHOWCASE_SUB_PAGE_PATH_REG_EXP);
  const encodedSlug = match?.[1];

  if (!encodedSlug) {
    return null;
  }

  try {
    return decodeURIComponent(encodedSlug);
  } catch {
    // A hand-edited URL can carry a malformed escape sequence that throws in decodeURIComponent.
    // The raw segment won't match any derived slug, so resolution falls back to the root page
    // rather than taking down the public page.
    return encodedSlug;
  }
};
